import type { Lab } from './types.js';

const DEG = Math.PI / 180;
const POW25_7 = Math.pow(25, 7);

/** Parametric weighting factors. The defaults (1,1,1) are the reference conditions. */
export interface DeltaE2000Weights {
  kL?: number;
  kC?: number;
  kH?: number;
}

/** Plain Euclidean distance in Lab (CIE 1976). Cheap; poor perceptual uniformity. */
export function deltaE76(a: Lab, b: Lab): number {
  return Math.hypot(a.L - b.L, a.a - b.a, a.b - b.b);
}

/**
 * CIEDE2000 colour difference.
 *
 * Implements CIE 15:2004 / Sharma, Wu & Dalal (2005), including the hue-angle
 * edge cases that the original CIE text leaves ambiguous:
 *
 *  - h' is taken as 0 when both a' and b are 0 (the atan2(0,0) case);
 *  - the mean hue h̄' degenerates to the plain sum when either chroma is 0,
 *    so that a neutral sample does not drag the mean toward an arbitrary angle;
 *  - the ±180 deg wrap is resolved before averaging, not after.
 *
 * Both inputs must be in the same illuminant. The function does not check that,
 * because it has no way to know — it is the caller's job.
 */
export function deltaE2000(lab1: Lab, lab2: Lab, weights: DeltaE2000Weights = {}): number {
  const { kL = 1, kC = 1, kH = 1 } = weights;

  const { L: L1, a: a1, b: b1 } = lab1;
  const { L: L2, a: a2, b: b2 } = lab2;

  const C1 = Math.hypot(a1, b1);
  const C2 = Math.hypot(a2, b2);
  const cBar = (C1 + C2) / 2;

  const cBar7 = Math.pow(cBar, 7);
  const G = 0.5 * (1 - Math.sqrt(cBar7 / (cBar7 + POW25_7)));

  const a1p = (1 + G) * a1;
  const a2p = (1 + G) * a2;

  const C1p = Math.hypot(a1p, b1);
  const C2p = Math.hypot(a2p, b2);

  const h1p = hueAngle(b1, a1p);
  const h2p = hueAngle(b2, a2p);

  const dLp = L2 - L1;
  const dCp = C2p - C1p;

  const chromaProduct = C1p * C2p;

  let dhp = 0;
  if (chromaProduct !== 0) {
    dhp = h2p - h1p;
    if (dhp > 180) dhp -= 360;
    else if (dhp < -180) dhp += 360;
  }
  const dHp = 2 * Math.sqrt(chromaProduct) * Math.sin((dhp / 2) * DEG);

  const lBarp = (L1 + L2) / 2;
  const cBarp = (C1p + C2p) / 2;

  let hBarp: number;
  if (chromaProduct === 0) {
    hBarp = h1p + h2p;
  } else if (Math.abs(h1p - h2p) <= 180) {
    hBarp = (h1p + h2p) / 2;
  } else if (h1p + h2p < 360) {
    hBarp = (h1p + h2p + 360) / 2;
  } else {
    hBarp = (h1p + h2p - 360) / 2;
  }

  const T =
    1 -
    0.17 * Math.cos((hBarp - 30) * DEG) +
    0.24 * Math.cos(2 * hBarp * DEG) +
    0.32 * Math.cos((3 * hBarp + 6) * DEG) -
    0.2 * Math.cos((4 * hBarp - 63) * DEG);

  const dTheta = 30 * Math.exp(-Math.pow((hBarp - 275) / 25, 2));

  const cBarp7 = Math.pow(cBarp, 7);
  const Rc = 2 * Math.sqrt(cBarp7 / (cBarp7 + POW25_7));
  const Rt = -Math.sin(2 * dTheta * DEG) * Rc;

  const lBarpMinus50Sq = Math.pow(lBarp - 50, 2);
  const Sl = 1 + (0.015 * lBarpMinus50Sq) / Math.sqrt(20 + lBarpMinus50Sq);
  const Sc = 1 + 0.045 * cBarp;
  const Sh = 1 + 0.015 * cBarp * T;

  const termL = dLp / (kL * Sl);
  const termC = dCp / (kC * Sc);
  const termH = dHp / (kH * Sh);

  return Math.sqrt(termL * termL + termC * termC + termH * termH + Rt * termC * termH);
}

/** atan2 in degrees, normalised to [0, 360), with the (0,0) case pinned to 0. */
function hueAngle(b: number, ap: number): number {
  if (b === 0 && ap === 0) return 0;
  const deg = Math.atan2(b, ap) / DEG;
  return deg < 0 ? deg + 360 : deg;
}

/**
 * Rough perceptual bands for a CIEDE2000 result. Useful for UI copy; these are
 * conventional rules of thumb, not part of the standard.
 */
export function describeDeltaE(deltaE: number): string {
  if (deltaE < 1) return 'Not perceptible';
  if (deltaE < 2) return 'Perceptible on close inspection';
  if (deltaE < 10) return 'Perceptible at a glance';
  if (deltaE < 50) return 'Clearly different';
  return 'Effectively opposite';
}
